import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../services/api';

/**
 * Custom hook to handle referral code from URL parameters
 * Stores ref code in backend cache for conversion tracking
 * 
 * Usage:
 *   const { refCode, schoolId, hasReferral } = useReferralCode();
 * 
 * The hook automatically:
 * 1. Extracts 'ref' and 'schid' from URL query parameters
 * 2. Calls backend to store ref code in cache (for IP-based tracking fallback)
 * 3. Returns refCode for passing to checkout session (recommended)
 * 4. Handles errors gracefully without blocking workflow
 */
export const useReferralCode = () => {
  const [searchParams] = useSearchParams();
  const [refCode, setRefCode] = useState<string | null>(null);
  const [schoolId, setSchoolId] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processRefCode = async () => {
      const refCodeParam = searchParams.get('ref');
      const schoolIdParam = searchParams.get('schid');

      // Extract and set parameters
      if (refCodeParam) {
        setRefCode(refCodeParam);
      }

      if (schoolIdParam) {
        setSchoolId(parseInt(schoolIdParam, 10));
      }

      // Store ref code in backend cache as fallback for IP-based tracking
      if (refCodeParam) {
        setIsProcessing(true);
        setError(null);

        try {
          // Try new API endpoint first (with school_id for better tracking)
          const schoolIdValue = schoolIdParam ? parseInt(schoolIdParam, 10) : undefined;
          await apiClient.storeReferralCode(refCodeParam, schoolIdValue);

          console.log('✓ Referral code processed:', refCodeParam);
        } catch (err) {
          // Log error but don't block user workflow
          const errorMsg = err instanceof Error ? err.message : 'Failed to process referral code';
          console.warn('⚠ Referral code processing failed:', errorMsg);
          setError(errorMsg);
        } finally {
          setIsProcessing(false);
        }
      }
    };

    processRefCode();
  }, [searchParams]);

  return {
    refCode,
    schoolId,
    isProcessing,
    error,
    hasReferral: !!(refCode || schoolId),
  };
};

export default useReferralCode;
